/**
 * Resolves the active LOCATION market from the selected store.
 * Storefront prices come from variant metafields (custom.price_*), not catalog JSON.
 * Fires maximize:catalog:ready so location-persistence / price renderers can react.
 */
if (!window.Maximize?.loadedScript?.includes("catalog-bootstrap.js")) {
  if (window.Maximize?.loadedScript) {
    window.Maximize.loadedScript.push("catalog-bootstrap.js");
  }

  (function () {
    const EVENT_READY = "maximize:catalog:ready";
    const EVENT_ERROR = "maximize:catalog:error";

    const MARKET_HANDLES = [
      "halifax",
      "halifax-location",
      "mississauga",
      "mississauga-location",
      "windsor",
      "windsor-location",
    ];

    function normalize(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/&amp;/g, "&")
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    function getConfig() {
      return (window.Maximize && window.Maximize.catalog) || {};
    }

    function getSelectedStoreName() {
      try {
        if (window.Alpine && Alpine.store("xStoreMap")?.currentStoreSelected) {
          return Alpine.store("xStoreMap").currentStoreSelected;
        }
      } catch (_) {
        /* Alpine may not be ready */
      }
      try {
        const saved = JSON.parse(localStorage.getItem("currentStoreSelected") || "null");
        return saved?.selectedStore || "";
      } catch (_) {
        return "";
      }
    }

    function resolveMarketHandle(markets, storeName) {
      const config = getConfig();
      const explicit = config.storeNameToHandle || {};
      const raw = String(storeName || "").trim();
      if (!raw) return null;

      if (explicit[raw]) return explicit[raw];

      const normalizedStore = normalize(raw);
      const byExplicitKey = Object.keys(explicit).find(
        (key) => normalize(key) === normalizedStore
      );
      if (byExplicitKey) return explicit[byExplicitKey];

      const locationMarkets = (markets || []).filter((m) => m.type === "LOCATION");

      for (const market of locationMarkets) {
        const name = normalize(market.name);
        const handle = normalize(market.handle);
        if (
          normalizedStore === name ||
          normalizedStore === handle ||
          normalizedStore.includes(name) ||
          name.includes(normalizedStore) ||
          normalizedStore.includes(handle.replace(/-/g, " "))
        ) {
          return market.handle;
        }
      }

      const keywords = ["mississauga", "windsor", "halifax"];
      for (const keyword of keywords) {
        if (!normalizedStore.includes(keyword)) continue;
        if (locationMarkets.length) {
          const found = locationMarkets.find((m) => {
            const handle = normalize(m.handle);
            const name = normalize(m.name);
            return handle.includes(keyword) || name.includes(keyword);
          });
          if (found) return found.handle;
        }
        const fallback = MARKET_HANDLES.find((h) => h.includes(keyword));
        if (fallback) return fallback;
      }

      return null;
    }

    document.addEventListener("alpine:init", () => {
      Alpine.store("xCatalog", {
        status: "idle",
        error: null,
        syncedAt: null,
        markets: [],
        activeMarket: null,
        activeCatalog: null,
        products: [],
        productsByHandle: Object.create(null),
        _watchTimer: null,
        _lastStoreName: null,

        async init() {
          if (this.status === "loading" || this.status === "ready") return;
          this.status = "loading";
          this.error = null;

          const config = getConfig();
          try {
            if (config.marketsUrl) {
              const res = await fetch(config.marketsUrl, { credentials: "same-origin" });
              if (res.ok) {
                const data = await res.json();
                this.markets = data.markets || [];
                this.syncedAt = data.syncedAt || null;
              }
            }
            // Markets file is optional — keyword matching still works without it.
            this.syncActiveCatalog();
            this._startStoreWatch();
          } catch (err) {
            this.status = "error";
            this.error = err.message || String(err);
            document.dispatchEvent(
              new CustomEvent(EVENT_ERROR, { detail: { error: this.error } })
            );
          }
        },

        _startStoreWatch() {
          if (this._watchTimer) return;
          this._lastStoreName = getSelectedStoreName();
          this._watchTimer = window.setInterval(() => {
            const current = getSelectedStoreName();
            if (current !== this._lastStoreName) {
              this._lastStoreName = current;
              this.syncActiveCatalog();
            }
          }, 500);

          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
              const current = getSelectedStoreName();
              if (current !== this._lastStoreName) {
                this._lastStoreName = current;
                this.syncActiveCatalog();
              }
            }
          });

          document.addEventListener("maximize:store-changed", (e) => {
            this._lastStoreName = e?.detail?.store || getSelectedStoreName();
            this.syncActiveCatalog();
          });
        },

        syncActiveCatalog() {
          const storeName = getSelectedStoreName();
          const handle = resolveMarketHandle(this.markets, storeName);

          if (!handle) {
            this.activeMarket = null;
            this.activeCatalog = null;
            this.products = [];
            this.productsByHandle = Object.create(null);
            this.status = "ready";
            document.dispatchEvent(
              new CustomEvent(EVENT_READY, {
                detail: { market: null, productCount: 0, storeName },
              })
            );
            return;
          }

          this.activeMarket =
            this.markets.find((m) => m.handle === handle) || {
              handle,
              name: handle,
              type: "LOCATION",
            };
          this.activeCatalog = null;
          this.products = [];
          this.productsByHandle = Object.create(null);
          this.status = "ready";
          this.error = null;

          document.dispatchEvent(
            new CustomEvent(EVENT_READY, {
              detail: {
                market: this.activeMarket,
                catalog: null,
                productCount: 0,
                storeName,
                source: "metafields",
              },
            })
          );
        },

        getProduct() {
          return null;
        },

        getVariantPrice() {
          return null;
        },

        isInActiveCatalog() {
          return false;
        },
      });

      queueMicrotask(() => {
        const store = Alpine.store("xCatalog");
        if (store && typeof store.init === "function") store.init();
      });
    });
  })();
}
